import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import School from '@mui/icons-material/School';
import Assignment from '@mui/icons-material/Assignment';
import Folder from '@mui/icons-material/Folder';
import MenuBook from '@mui/icons-material/MenuBook';
import Public from '@mui/icons-material/Public';
import Description from '@mui/icons-material/Description';
import SwapCalls from '@mui/icons-material/SwapCalls';

import { isFeatureEnabled, type FeatureFlagName } from '@/shared/libs/featureFlags';

type DashboardItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
  flag?: FeatureFlagName;
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const myCurriculumItems: DashboardItem[] = [
    {
      icon: <MenuBook sx={{ fontSize: 24, color: 'primary.main' }} />,
      title: 'Courses',
      description: 'View and manage your created courses',
      path: '/curriculum/courses',
    },
    {
      icon: <School sx={{ fontSize: 24, color: 'primary.main' }} />,
      title: 'Standards',
      description: 'Educational standards frameworks',
      path: '/curriculum/standards',
    },
    {
      icon: <Assignment sx={{ fontSize: 24, color: 'primary.main' }} />,
      title: 'Rubrics',
      description: 'Assessment rubrics and evaluation tools',
      path: '/curriculum/rubrics',
      flag: 'rubrics',
    },
    {
      icon: <SwapCalls sx={{ fontSize: 24, color: 'primary.main' }} />,
      title: 'Vertical Alignment',
      description: 'Cross-grade progression planning',
      path: '/vertical-alignment',
      flag: 'verticalAlignment',
    },
    {
      icon: <Folder sx={{ fontSize: 24, color: 'primary.main' }} />,
      title: 'Resources',
      description: 'Your educational resources and materials',
      path: '/resources',
      flag: 'resources',
    },
  ];

  const curriculumLibraryItems: DashboardItem[] = [
    {
      icon: <Public sx={{ fontSize: 24, color: 'secondary.main' }} />,
      title: 'Open Educational Resources',
      description: 'Browse freely available educational content',
      path: '/library/oer',
      flag: 'librarySurfaces',
    },
    {
      icon: <Description sx={{ fontSize: 24, color: 'secondary.main' }} />,
      title: 'Resource Library',
      description: 'Student-facing resource templates with AI generation',
      path: '/library/templates',
      flag: 'librarySurfaces',
    },
  ];

  const visibleMyCurriculumItems = myCurriculumItems.filter(
    (item) => !item.flag || isFeatureEnabled(item.flag)
  );
  const visibleCurriculumLibraryItems = curriculumLibraryItems.filter(
    (item) => !item.flag || isFeatureEnabled(item.flag)
  );

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.75rem' } }}
          >
            Welcome to Your Curriculum Dashboard
          </Typography>
        </Box>

        {/* Two Column Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: visibleCurriculumLibraryItems.length > 0 ? '1fr 1fr' : '1fr',
            },
            gap: 4,
            alignItems: 'stretch',
          }}
        >
          {/* My Curriculum Section - Left Column */}
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="My Curriculum"
              sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}
            />
            <CardContent>
              <Typography variant="body1">
                Manage your courses, standards, resources, and more
              </Typography>
              <List>
                {visibleMyCurriculumItems.map((item, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton onClick={() => navigate(item.path)}>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.title} secondary={item.description} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Curriculum Library Section - Right Column */}
          {visibleCurriculumLibraryItems.length > 0 && (
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title="Curriculum Library"
                sx={{ backgroundColor: 'secondary.main', color: 'secondary.contrastText' }}
              />
              <CardContent>
                <Typography variant="body1">
                  Discover and access shared educational resources
                </Typography>
                <List>
                  {visibleCurriculumLibraryItems.map((item, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemButton onClick={() => navigate(item.path)}>
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.title} secondary={item.description} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default LandingPage;
